import { useMutation, useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useEffect, useRef, useState } from "react";
import { Link, Route, Switch } from "wouter";
import { SELF_SELECTABLE_ROLES, type Role, type SelfSelectableRole } from "@titleverify/shared";
import { apiErrorText, apiFetch, ApiError } from "./api.js";
import { useAuth } from "./auth.js";
import { EnvironmentNotice } from "./EnvironmentNotice.js";
import { SUPPORT_EMAIL } from "./support.js";
import { ErrorBoundary } from "./ErrorBoundary.js";
// `useMe` lives in its own module rather than here because the pages below need
// the role too, and a page importing back from the app shell that renders it
// would close an import cycle.
import { useMe } from "./me.js";
import { AdminAccess } from "./pages/AdminAccess.js";
import { AdminCases } from "./pages/AdminCases.js";
import { CaseDetail } from "./pages/CaseDetail.js";
import { AdminOnly } from "./AdminOnly.js";
import { AdminHome } from "./pages/AdminHome.js";
import { AdminPanel } from "./pages/AdminPanel.js";
import { AdminProfessionals } from "./pages/AdminProfessionals.js";
import { AdminRegistry } from "./pages/AdminRegistry.js";
import { AdminReports } from "./pages/AdminReports.js";
import { AdminRoster } from "./pages/AdminRoster.js";
import { ClientCases } from "./pages/ClientCases.js";
import { DataHandling } from "./pages/DataHandling.js";
import { Professional } from "./pages/Professional.js";

/**
 * What each self-selectable account type actually gets you.
 *
 * The professional line is the one that matters. Signing up as a professional
 * creates an UNVERIFIED profile that is assignable to nothing until an admin
 * checks the enrolment against a named source (COMPLIANCE Constraint 5). Saying
 * so at the point of choosing is the difference between an honest funnel and one
 * that lets an attorney believe they have standing here.
 *
 * Keyed off SELF_SELECTABLE_ROLES rather than a second hand-written list, so a
 * role added to the shared constant with no copy here fails to compile instead
 * of silently rendering an unexplained radio button.
 */
const ACCOUNT_TYPE_COPY: Record<SelfSelectableRole, { label: string; consequence: string }> = {
  client: {
    label: "I want a land title verified",
    consequence:
      "You can ask for a title to be checked, and follow it as it goes. You will see your own " +
      "cases and your own documents — and only yours.",
  },
  professional: {
    label: "I am an Attorney-at-Law or Notary",
    consequence:
      "This grants you nothing on its own. It creates an unverified profile that is assignable " +
      "to no case at all. An admin must first check your enrolment against a named source — " +
      "until they have, you will not be routed any work.",
  },
};

/**
 * The account exists in Firebase but not here, carried across the signed-out →
 * signed-in boundary.
 *
 * It has to live at this level because Firebase signs the new user in the moment
 * the identity is created — so the sign-up form is unmounted before the
 * provisioning call it started can report back. Without this, the failure would
 * surface as a bare "not set up yet" with no reason, which is the exact
 * half-created state a real user hit in staging.
 */
type HalfCreated = {
  accountType: SelfSelectableRole;
  /** A finished sentence from `apiErrorText`, never a raw `.message` or error token. */
  reason: string;
};

function Home({ halfCreated }: { halfCreated: HalfCreated | null }) {
  const { data, error, isPending } = useMe();

  if (isPending) return <p role="status">Loading…</p>;

  if (error) {
    // A 401 FROM OUR API has a specific meaning worth saying out loud: the
    // identity is real but has no account on this platform yet. That is a
    // different problem from "something broke", and saying "something went wrong"
    // when we know exactly what is wrong would be dishonest.
    //
    // `fromApi` is the guard, and it was added because the opposite dishonesty
    // happened first. Cloud Run refused to invoke the service and returned its
    // own 401; this branch could not tell it apart and told the user their
    // account was not set up. It was — the request never reached us. Claiming a
    // cause we have not established is the same failure as claiming a title fact
    // we have not verified, and it wasted a real person's time.
    /*
     * ...AND ONLY WHEN THE SERVER SAYS THE ACCOUNT IS THE PROBLEM.
     *
     * `fromApi` was necessary and not sufficient. Our own API answers 401 for
     * two unrelated things: a valid token with no TitleVerify account behind it,
     * and a session that is expired, revoked or malformed. This branch could not
     * tell them apart, so someone whose session had simply lapsed — after a
     * password change, or a token revoked — was told their account was not set
     * up, and handed a button that then failed because the account was fine.
     *
     * The server now tags the one case a client can act on. Anything else falls
     * through to apiErrorText, which says the session ended and to sign in again.
     */
    if (error instanceof ApiError && error.status === 401 && error.fromApi) {
      if (error.code === "no_account") {
        return <FinishSetup halfCreated={halfCreated} />;
      }
      return (
        <p role="alert">
          Your session has ended — that can happen after a password change, or simply with
          time. Sign in again and your requests will be where you left them.
        </p>
      );
    }
    /*
     * EVERYTHING ELSE GOES THROUGH apiErrorText, and this screen in particular
     * must, because it is the gate every signed-in user passes before any other.
     * The two hand-rolled branches that used to be here both lied:
     *
     *  - They printed `error.status` raw. `NO_RESPONSE` is 0 — an internal
     *    sentinel for "fetch itself rejected, nothing ever answered" — so a
     *    dropped connection rendered "Could not reach the TitleVerify service
     *    (0)", quoting at the user a status the wire never produced. A 200 that
     *    would not parse (a hosting rewrite answering with index.html) rendered
     *    "(200)": a success code presented as unreachability, a sentence that
     *    argues with itself.
     *  - The fallback printed `error.message`, which on our own 5xx is the bare
     *    token "internal_error" — a log string, not an account of what happened,
     *    handed to a user as though it were one.
     *
     * apiErrorText already draws every one of those distinctions in one place.
     * Restating them here was how they drifted.
     */
    return <p role="alert">{apiErrorText(error, "Could not load your account.")}</p>;
  }

  return <RoleHome role={data.role} />;
}

/**
 * What each role lands on.
 *
 * Routing by role from the /me query rather than showing everyone every link.
 * Hiding a link from a non-admin is a COURTESY, NOT A CONTROL, and it is worth
 * being exact about which: the server refuses every admin action to every other
 * role, so a client who types /admin/roster gets a 403 from the API rather than
 * a page of data. What the hiding buys is an honest interface — a client should
 * not be shown a door they cannot open, and a client should never see anything
 * that looks like a list of professionals (COMPLIANCE Constraint 1).
 *
 * The switch is exhaustive over Role and falls through to an honest unknown
 * rather than to a default that guesses. `search_agent` genuinely has no surface
 * yet; saying so is better than an empty page that looks broken.
 */
function RoleHome({ role }: { role: Role }) {
  /*
   * ONE PAGE, ONE HEADING, ONE COLUMN — and why this is not what it was.
   *
   * This used to open every role's home with a "Your account" page-head and a
   * <dl> printing the raw user UUID, and only then render the role's actual
   * screen. For a client that produced a page with TWO competing page-heads
   * stacked on top of each other — "Your account", immediately followed by
   * ClientCases' own "Open a verification request" — separated by a debug
   * readout. It read as an unfinished screen because it was one: an account
   * dump with the product underneath it.
   *
   * It also caused the ragged left edge. `main > *` gives each DIRECT child the
   * reading measure, so the header and the <dl> were laid out as siblings of the
   * page while everything ClientCases renders sits one level deeper inside its
   * <section>. Two nesting depths, two different edges, on one screen.
   *
   * So each role now returns exactly one thing, and the client's home IS their
   * requests rather than a preamble to them.
   *
   * THE UUID IS GONE ON PURPOSE. `ed077fbf-5e24-4718-9266-25d85f174f11` means
   * nothing to the person reading it, the masthead already shows who they are
   * signed in as, and when a client needs to quote something to us it is their
   * CASE reference, not their user id — that is what the outcome panel gives
   * them. /data is where what-we-hold-about-you belongs, and it describes both
   * fields there.
   */
  if (role === "client") return <ClientCases />;

  if (role === "professional") {
    return (
      <section>
        <header className="page-head">
          <p className="eyebrow">Your work</p>
          <h2>Cases and verification</h2>
          <p className="lede">
            Your profile, where your enrolment check has got to, and any case assigned to you.
          </p>
        </header>
        <p>
          <Link href="/professional">Open your professional portal</Link>
        </p>
      </section>
    );
  }

  if (role === "admin") {
    return (
      <section>
        <header className="page-head">
          <p className="eyebrow">Internal operations</p>
          <h2>Run the platform</h2>
          <p className="lede">
            Enrolment checks, the case queue, the registry table, and reports from clients.
          </p>
        </header>
        <p>
          <Link href="/admin">Open the admin portal</Link>
        </p>
      </section>
    );
  }

  return (
    <section>
      <header className="page-head">
        <p className="eyebrow">Search agent</p>
        <h2>Nothing here yet</h2>
      </header>
      <p className="empty">
        There is no search-agent surface yet. Your account is real and your role is correct — the
        screens for retrieving folios and extracts have not been built.
      </p>
      {/*
        The one screen this role CAN use, offered so the page is not a dead end.
        It is also the honest one for them: a search agent is granted sight of
        registry extracts and never of a client's own uploads, and /data says so.
      */}
      <p>
        <Link href="/data">What this platform holds, and what your role can see</Link>
      </p>
    </section>
  );
}

/**
 * Recovery for a sign-in that has no account behind it.
 *
 * Two ways to arrive: the provisioning call failed during sign-up (then
 * `halfCreated` carries the server's reason), or the user closed the tab and
 * came back later (then it does not, and we say only what we know). Both are the
 * same fix — provision the account — so there is one screen, not two.
 */
function FinishSetup({ halfCreated }: { halfCreated: HalfCreated | null }) {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  const [accountType, setAccountType] = useState<SelfSelectableRole>(
    halfCreated?.accountType ?? "client",
  );

  const provision = useMutation({
    mutationFn: async () => {
      try {
        return await apiFetch<{ userId: string; role: Role }>("/accounts", {
          token: await getToken(),
          method: "POST",
          body: { accountType },
        });
      } catch (err) {
        /*
         * A 409 HERE IS SUCCESS, NOT A FAILURE, and treating it as one is what
         * turned this recovery screen into a dead end.
         *
         * This button exists to reach one end state: an account exists for this
         * identity. "An account already exists for this identity" IS that end
         * state. Reporting it as an error told a user whose account was
         * perfectly fine that setup had failed again, and left them on a screen
         * whose only working exit was a page reload nothing suggested.
         *
         * Reproduced before this fix: sign up, land here (see the race
         * documented in SignUp above), press the button, get "Still could not
         * set up the account. An account already exists for this identity."
         * Stuck. A manual reload showed a fully working account the whole time.
         *
         * It also makes the button idempotent, which matters beyond the race: a
         * double-click, a retried request, or a user returning to a stale tab
         * all land here, and all of them mean the same harmless thing.
         *
         * `fromApi` is required for the same reason it is on the 401 branch in
         * Home: only a 409 our own API produced licenses this conclusion. A 409
         * from something in front of the service is not evidence about the
         * caller's account.
         */
        if (err instanceof ApiError && err.status === 409 && err.fromApi) return null;
        throw err;
      }
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["me"] }),
  });

  return (
    <section className="auth-card auth-card-wide">
      <p className="eyebrow">One step left</p>
      <h2>Your account is not set up yet</h2>
      <p>
        Your sign-in works — the email and password are real and Firebase accepted them. What is
        missing is the TitleVerify account behind it, so there is nothing here to show you yet.
        Nothing was lost; the step below is the one that did not complete.
      </p>
      {/*
        `reason` arrives from SignUp already classified by apiErrorText — a
        sentence, not a machine token — so it is rendered as one rather than
        embedded mid-clause.
      */}
      {halfCreated ? (
        <p role="alert">
          {halfCreated.reason} You do not need to sign up again — use the same sign-in from now on
          and finish here.
        </p>
      ) : null}

      <AccountTypeChoice value={accountType} onChange={setAccountType} />

      {/*
        Through apiErrorText like every other failure surface. Printing
        `.message` put "internal_error" on screen as the platform's account of
        why the account could not be created — and this is the screen where the
        user has least context to reinterpret it.
      */}
      {provision.error ? (
        <p role="alert">{apiErrorText(provision.error, "Still could not set up the account.")}</p>
      ) : null}

      <button type="button" disabled={provision.isPending} onClick={() => provision.mutate()}>
        {provision.isPending ? "Setting up…" : "Finish setting up my account"}
      </button>
    </section>
  );
}

/**
 * The account-type choice, shared by sign-up and the recovery screen.
 *
 * The options come from SELF_SELECTABLE_ROLES — the same constant the server
 * validates `accountType` against — so the UI cannot drift into offering
 * something the server will refuse, and `admin` and `search_agent` are not
 * merely hidden here, they are absent from the list this renders.
 */
function AccountTypeChoice({
  value,
  onChange,
}: {
  value: SelfSelectableRole;
  onChange: (value: SelfSelectableRole) => void;
}) {
  return (
    <fieldset>
      <legend>What are you here as?</legend>
      {SELF_SELECTABLE_ROLES.map((role) => (
        <p key={role}>
          <label>
            <input
              type="radio"
              name="accountType"
              value={role}
              checked={value === role}
              onChange={() => onChange(role)}
            />{" "}
            {ACCOUNT_TYPE_COPY[role].label}
          </label>
          <br />
          <small>{ACCOUNT_TYPE_COPY[role].consequence}</small>
        </p>
      ))}
    </fieldset>
  );
}

function SignIn() {
  const { signIn, sendPasswordReset } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  /*
   * THE RECOVERY PATH THAT DID NOT EXIST.
   *
   * There was no reset link, no reset route, and nothing in AuthState that could
   * send one — `sendPasswordResetEmail` ships in the Firebase SDK this app
   * already depends on and was never imported. A client who forgot their
   * password lost the case, the reference, the deed scans they uploaded and the
   * eventual opinion, on a platform that holds no telephone number for them and
   * sends nothing. Their only move was a second account, which starts a new
   * request and orphans the first.
   */
  const [resetState, setResetState] = useState<"idle" | "sending" | "sent">("idle");

  async function requestReset() {
    const address = email.trim();
    if (!address) {
      setError("Type the email address you signed up with, then choose this again.");
      return;
    }
    setResetState("sending");
    setError(null);
    try {
      await sendPasswordReset(address);
      setResetState("sent");
    } catch {
      // Only a transport failure reaches here — an unknown address resolves
      // successfully on purpose (see AuthState.sendPasswordReset). So this
      // message is about the request not leaving, which is the one thing the
      // user can act on.
      setResetState("idle");
      setError("Could not send the reset email just now. Check your connection and try again.");
    }
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await signIn(email, password);
    } catch {
      // Deliberately one message for every failure. Distinguishing "no such
      // account" from "wrong password" tells an attacker which emails are
      // registered — and on this platform, being registered means owning land
      // someone is checking.
      setError("That did not work. Please check the email and password, and try again.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="auth-card" onSubmit={submit}>
      <h2>Welcome back</h2>
      <p className="muted">Sign in to pick up where you left off.</p>
      {error ? <p role="alert">{error}</p> : null}
      <label htmlFor="email">Email</label>
      <input
        id="email"
        type="email"
        autoComplete="username"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        required
      />
      <label htmlFor="password">Password</label>
      <input
        id="password"
        type="password"
        autoComplete="current-password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        required
      />
      <button type="submit" disabled={busy}>
        {busy ? "Signing in…" : "Sign in"}
      </button>

      {/*
        SAME ANSWER WHETHER OR NOT THE ADDRESS IS REGISTERED. The confirmation
        below says "if there is an account" rather than "we have sent" — because
        this form must not become a way to ask whether a given person has a land
        purchase in progress here. It is the same reasoning as the single
        sign-in error message above.
      */}
      {resetState === "sent" ? (
        <p role="status" className="muted">
          If there is an account for that address, a reset link is on its way to it. The link
          comes from Firebase, our sign-in provider, and it expires. Nothing else about your
          request is in that email.
        </p>
      ) : (
        <p className="muted">
          <button type="button" className="linkish" onClick={requestReset} disabled={resetState === "sending"}>
            {resetState === "sending" ? "Sending…" : "Forgotten your password?"}
          </button>{" "}
          A reset link goes to the address above, if there is an account for it. It comes
          from Firebase, our sign-in provider, and not from this platform — which writes to you
          about a request only when an attorney's opinion on it is ready.
        </p>
      )}
    </form>
  );
}

/**
 * Creating an account is TWO writes to two systems: a Firebase identity, then a
 * TitleVerify account. Only the second one knows what kind of account this is,
 * and only the first one can be retried by signing in again. They can therefore
 * disagree, and the honest thing is to design for that rather than assume it
 * away — a real user in staging ended up with the identity and no account, and
 * was shown a generic error that told them nothing about what to do next.
 *
 * So: if step two fails we do not claim the sign-up failed (it half didn't), and
 * we do not delete the identity to tidy up (we cannot, from the client, and
 * trying would risk destroying a working sign-in). We hand the reason to
 * `FinishSetup`, which is where the user is about to land anyway.
 */
function SignUp({ onHalfCreated }: { onHalfCreated: (state: HalfCreated) => void }) {
  const { createIdentity, getToken } = useAuth();
  const queryClient = useQueryClient();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [accountType, setAccountType] = useState<SelfSelectableRole>("client");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);

    // Step one: the identity.
    try {
      await createIdentity(email, password);
    } catch (identityError) {
      // Nothing was created, so this one is safe to report plainly — and worth
      // reporting plainly, because "password too short" and "email already in
      // use" are both things only the user can fix. Unlike sign-in, an
      // already-in-use email is not a disclosure we control: Firebase refuses
      // the duplicate regardless of what we render.
      setError(
        identityError instanceof Error && identityError.message
          ? `Could not create the sign-in: ${identityError.message}`
          : "Could not create the sign-in.",
      );
      setBusy(false);
      return;
    }

    // Step two: the account. The identity now exists whatever happens next.
    try {
      await apiFetch<{ userId: string; role: Role }>("/accounts", {
        token: await getToken(),
        method: "POST",
        body: { accountType },
      });
      /*
       * THE SUCCESS BRANCH THAT USED TO BE MISSING, and its absence was a
       * first-run blocker rather than an untidiness.
       *
       * `createIdentity` above signs the new user in the INSTANT the Firebase
       * identity exists, so the app re-renders into the signed-in tree while
       * this provisioning call is still in flight. `Home` mounts, `useMe()`
       * fires, and the server correctly answers 401 — there genuinely is no
       * account yet. `useMe` is `retry: false`, so that 401 is cached as a
       * terminal error and nothing ever asks again.
       *
       * Then this call succeeds. The account now exists, and the screen still
       * says "Your account is not set up yet", because no one re-asked.
       *
       * Reproduced end to end before this fix: signed up as a client, account
       * created, UI showed the not-set-up screen, and its own "Finish setting
       * up my account" button then failed with "An account already exists for
       * this identity" — a dead end whose only exit was a manual page reload
       * that nothing on screen suggested.
       *
       * Invalidating `["me"]` is the whole fix for the common path: the errored
       * query refetches, gets the account that now exists, and the user lands
       * where they were going. The 409 handling in FinishSetup below is the
       * belt to this braces — it catches the same dead end arriving by any
       * other route.
       */
      await queryClient.invalidateQueries({ queryKey: ["me"] });
    } catch (accountError) {
      // Classified HERE, at the throw site, rather than passed raw for a later
      // screen to print. `.message` on our own 5xx is the token "internal_error",
      // and that token was travelling across the signed-out → signed-in boundary
      // to be rendered on FinishSetup as the reason the account does not exist.
      // `reason` is now a finished sentence, which is also what makes it safe for
      // that screen to render without knowing where it came from.
      onHalfCreated({
        accountType,
        reason: apiErrorText(accountError, "Setting up your TitleVerify account did not complete."),
      });
    } finally {
      setBusy(false);
    }
    // No success branch: Firebase has already signed the new user in, and the
    // app is about to re-render into the signed-in tree on its own.
  }

  return (
    <form className="auth-card auth-card-wide" onSubmit={submit}>
      <h2>Create your account</h2>
      <p className="muted">
        An email address and a password are all it takes to start. You will be asked what
        brings you here, because that decides what your account can see.
      </p>
      {error ? <p role="alert">{error}</p> : null}
      <label htmlFor="signup-email">Email</label>
      <input
        id="signup-email"
        type="email"
        autoComplete="username"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        required
      />
      <label htmlFor="signup-password">Password</label>
      <input
        id="signup-password"
        type="password"
        autoComplete="new-password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        required
      />

      <AccountTypeChoice value={accountType} onChange={setAccountType} />

      <button type="submit" disabled={busy}>
        {busy ? "Creating your account…" : "Create account"}
      </button>
    </form>
  );
}

/**
 * WHAT A STRANGER SEES. READ COMPLIANCE.md BEFORE CHANGING A WORD OF IT.
 * ===========================================================================
 *
 * Until this existed, someone arriving at the site saw a login box and no
 * statement of what they had arrived at. It is now the most-read page in the
 * product and it is a compliance surface, governed by three hard limits:
 *
 *  1. NO NAMED, RANKED, RATED OR FEATURED PROFESSIONAL, and nothing that reads
 *     as a directory to browse. Advertising to attract clientele for an attorney
 *     is touting under Supreme Court Rule 39 and can end in disenrolment
 *     (COMPLIANCE Constraint 1). This page describes the SERVICE and the STEPS.
 *  2. NO PRICE AND NO FEE CLAIM OF ANY KIND — not a number, not a range, not
 *     "from". Which side charges the client for what is open decision D1 and is
 *     the reviewing attorney's to answer; a figure here would pre-empt it.
 *  3. NO OUTCOME AND NO TURNAROUND PROMISE, and not one word that states or
 *     implies a fact about any parcel. The platform has no view on any piece of
 *     land and never will (CLAUDE.md non-negotiables 1 and 2).
 *  4. NO SECURITY, PRIVACY OR RETENTION CLAIM THAT IS NOT TRUE OF THE CODE
 *     TODAY — the same rule the DataHandling page states about itself, added
 *     here because this page broke it twice in one sentence and a rule that
 *     lives only on the page nobody edits is not a rule. A safeguard described
 *     as stronger than it is, is worse than describing none: the reader relies
 *     on it. If the mechanism is not in this repository now, it does not go on
 *     this page, however certain it is that MD6 will ship it.
 *
 * There is also no photograph, here or anywhere. A picture of land on this page
 * would imply we know something about land. We do not.
 *
 * TONE — REWRITTEN, AND THE REWRITE IS AS DELIBERATE AS THE LIMITS ABOVE.
 * ---------------------------------------------------------------------------
 * This page used to lead its most prominent block with the heading "What this
 * platform does not do", followed by four refusals. Every sentence was true and
 * every one still is, in substance — but a stranger's first impression of a
 * service they are considering trusting with the largest purchase of their life
 * was a list of things we decline to do. It read as a shield held against them,
 * and it undersold a service that is genuinely worth having: a real attorney, in
 * the right registry jurisdiction, tracing the actual records.
 *
 * So the facts were kept and the frame was turned round. THE RULE FOR DOING THAT,
 * and the only one that makes it safe: a refusal may be rewritten as a
 * reassurance ONLY where the reassuring version is EXACTLY as true. "It does not
 * tell you a parcel is clear" is, from the client's side, "the answer comes from
 * a qualified person who read the actual registry records, not from software
 * guessing" — same fact, warmer, and more informative. That reframe is legitimate.
 *
 * Where a limit has NO warm reading, it is still said plainly, because the warm
 * version would be a lie. We cannot promise an outcome or a date; there is no
 * phrasing of that which is comforting AND true, so it is said kindly and left
 * uncomforting. WARMTH IS NEVER A LICENCE TO SOFTEN A FACT — every false claim
 * this product has shipped was comforting, which is precisely why it survived
 * review. A reader checking for lies looks for discouraging ones.
 */
function Landing() {
  return (
    <section className="landing">
      <div className="hero">
        <p className="eyebrow">Sri Lanka</p>
        <h1 className="hero-title">Have the land checked before you pay.</h1>
        <hr className="hero-rule" />
        <p className="hero-lede">
          Ask an attorney-at-law to trace a parcel&rsquo;s records at the land registry. You
          send the request; we route it and hold your papers privately while it runs.
        </p>
      </div>

      <h2>How it works</h2>
      <ol className="steps">
        <li className="step">
          <p className="step-index">01</p>
          <h3>Send a request</h3>
          <p>Describe the land. Add whatever papers you have — a part-filled form is fine.</p>
        </li>
        <li className="step">
          <p className="step-index">02</p>
          <h3>We route it</h3>
          <p>To the land registry with jurisdiction over the parcel — not over you.</p>
        </li>
        <li className="step">
          <p className="step-index">03</p>
          <h3>An attorney searches</h3>
          <p>Assigned only after an admin has checked their enrolment against a named source.</p>
        </li>
      </ol>

      {/*
        THE LIMITS, KEPT SHORT ON PURPOSE.
        These are the legal boundaries the product is built around (CLAUDE.md
        non-negotiables 1-3, COMPLIANCE Constraint 1). They used to run to four
        paragraphs, which meant nobody read them. One line each is more likely to
        be read, and an unread disclaimer protects nobody.
        Every line is checked against the code. Do not add one that is not.
      */}
      <section className="slab">
        <p className="eyebrow">What we cannot do</p>
        <h2>The law sets these, not us.</h2>
        <ul className="limits">
          <li>
            <strong>Only an attorney-at-law gives the opinion.</strong> This platform never
            writes one.
          </li>
          <li>
            <strong>We hold no view on any parcel.</strong> Unknown is shown as unknown.
          </li>
          <li>
            <strong>You do not choose the attorney.</strong> Where your land is registered
            decides who can act. We rank and advertise nobody.
          </li>
          <li>
            <strong>No outcome or date is promised.</strong> A search finds what the registry
            holds.
          </li>
        </ul>
      </section>
    </section>
  );
}

function SignedOut({ onHalfCreated }: { onHalfCreated: (state: HalfCreated) => void }) {
  const [mode, setMode] = useState<"signIn" | "signUp">("signIn");

  return (
    <>
      <Landing />
      <nav className="auth-switch" aria-label="Sign in or create an account">
        <button type="button" disabled={mode === "signIn"} onClick={() => setMode("signIn")}>
          Sign in
        </button>
        <button type="button" disabled={mode === "signUp"} onClick={() => setMode("signUp")}>
          Create an account
        </button>
      </nav>
      {mode === "signIn" ? <SignIn /> : <SignUp onHalfCreated={onHalfCreated} />}
    </>
  );
}

/**
 * The seal. Inline SVG, not an image file: nothing is fetched, it inherits
 * colour so it works in both themes without a second asset, and it survives a
 * strict connect-src. Two rings and a traced parcel boundary — a registry mark
 * rather than a logo, and deliberately abstract. It depicts no real land.
 */
function BrandSeal() {
  return (
    <svg className="brand-seal" viewBox="0 0 32 32" aria-hidden="true" focusable="false">
      <circle cx="16" cy="16" r="15" />
      <circle cx="16" cy="16" r="12.2" />
      <path className="seal-parcel" d="M10 21.2 L13 10.8 L22.4 13 L19.4 23.4 Z" />
      <path className="seal-parcel" d="M10 21.2 L22.4 13" />
    </svg>
  );
}

/**
 * The role-aware section links in the chrome.
 *
 * Reads the SAME ["me"] query the home page does, so TanStack de-duplicates it —
 * this adds no request and no behaviour. It renders nothing at all until that
 * query has succeeded, which is what keeps it silent on the sign-in screen and
 * on the not-set-up-yet screen, where there is no role to be aware of.
 *
 * Hiding a link from a non-admin is a COURTESY, NOT A CONTROL. The server
 * refuses every admin action to every other role, so a client who types
 * /admin/roster gets a 403 from the API rather than a page of data. What the
 * hiding buys is an honest interface — and, specifically, that a client is never
 * shown anything resembling a list of professionals (COMPLIANCE Constraint 1).
 */
function PortalNav() {
  const { data } = useMe();
  if (!data) return null;

  return (
    <nav className="masthead-nav" aria-label="Sections">
      <Link href="/">Home</Link>
      {data.role === "professional" ? <Link href="/professional">Your profile</Link> : null}
      {data.role === "admin" ? <Link href="/admin">Admin portal</Link> : null}
      {/*
        Not role-gated. A page about what the platform holds and who can read it
        should not be something a person has to already know the URL of, and it
        is the same true answer for every role.
      */}
      <Link href="/data">Your data</Link>
    </nav>
  );
}

/**
 * Typed structurally rather than against Firebase's `User`, because all the
 * chrome needs is an email to display. Keeping the provider's type out of the
 * shell is the same instinct as `IdentityVerifier` on the server.
 */
type SignedInIdentity = { email: string | null };

function Masthead({
  user,
  onSignOut,
}: {
  user: SignedInIdentity | null | undefined;
  onSignOut: () => void;
}) {
  return (
    <header className="masthead">
      <div className="masthead-inner">
        <Link href="/" className="brand">
          <BrandSeal />
          <span className="brand-text">
            <span className="brand-name">TitleVerify SL</span>
            <span className="brand-tag">Land title verification for buyers</span>
          </span>
        </Link>

        <span className="masthead-spacer" />

        {user ? (
          <>
            <PortalNav />
            <div className="identity">
              <span className="identity-email">{user.email}</span>
              <button type="button" className="quiet compact" onClick={onSignOut}>
                Sign out
              </button>
            </div>
          </>
        ) : null}
      </div>
    </header>
  );
}

/**
 * The standing statement of what the platform is, on every page including the
 * ones a client reaches mid-case. Same three limits as the landing copy, in one
 * breath — see the comment on `Landing` before editing either.
 */
function SiteFooter() {
  return (
    <footer className="site-footer">
      <div className="footer-inner">
        {/*
          THE DISCLAIMER STAYS WHOLE — it is on every page including the ones a
          client reaches mid-case, and a reviewing attorney should find it there.
          Only its manner changed: it now reads as us telling you where we stand,
          rather than as a notice posted against you. See the tone note on
          `Landing`. Nothing was dropped: no practice of law, no legal advice, no
          opinion of our own, client-initiated only, nobody advertised or ranked.
        */}
        <p className="footer-note">
          TitleVerify SL brings together a buyer who wants a land title checked and an enrolled
          attorney-at-law who can check it. We do not practise law, we give no legal advice, and
          we write no title opinion of our own — that is the attorney&rsquo;s, and it is theirs
          to stand behind.
        </p>
        <p className="footer-note">
          Every request here begins with the client asking. No professional is advertised,
          ranked or promoted on this platform.
        </p>
        {/*
          THE ONLY PLACE A PERSON CAN WRITE TO — and nothing at all until there is
          somewhere real to send them. Six strings in this app tell a client to
          "tell us" or "contact us"; before this there was no address, no number
          and no contact page, and the one control that exists is refused by the
          server on any case without an assigned professional.

          Rendered ONLY when configured. An address nobody reads is worse than a
          visible dead end: it turns a failure the user can see into one they
          cannot, and they wait on a reply that never comes. See support.ts.
        */}
        {SUPPORT_EMAIL ? (
          <p className="footer-note">
            Something wrong, or a question this site does not answer?{" "}
            <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>. A person reads it. We
            cannot give legal advice — that is your attorney&rsquo;s to give, not ours.
          </p>
        ) : null}
      </div>
    </footer>
  );
}

export function App() {
  const { user, signOut } = useAuth();
  const [halfCreated, setHalfCreated] = useState<HalfCreated | null>(null);

  /**
   * Forget the half-created signup when the signed-in identity changes.
   *
   * It describes ONE person's failed provisioning, and it outlives the component
   * that produced it on purpose (Firebase signs the new user in immediately, so
   * SignUp unmounts before its own fetch resolves). Without clearing it, one
   * person half-creating an account, signing out, and a DIFFERENT unprovisioned
   * person signing in on the same browser would show the second person the
   * first's failure reason — a stale message about someone else's account,
   * presented as if it were their own. Keyed on uid rather than cleared only on
   * sign-out, so switching accounts without a full sign-out is covered too.
   */
  const uid = user?.uid ?? null;
  const shownFor = useRef<string | null>(null);
  useEffect(() => {
    if (shownFor.current !== null && shownFor.current !== uid) setHalfCreated(null);
    shownFor.current = uid;
  }, [uid]);

  return (
    <div className="app">
      <Masthead user={user} onSignOut={() => void signOut()} />

      {/* Above everything, on every non-production deployment: a visitor must be
          able to read what this environment is before typing anything real. */}
      <EnvironmentNotice />

      <main>
        {/*
          INSIDE the shell, not around it. A render crash below here loses the
          page; it must not lose the masthead, the sign-out, or the link to what
          we hold about you. Losing the screen you were on is recoverable —
          losing the way out of the application is not.

          Keyed on the signed-in identity so a crash does not persist across a
          sign-out and follow the next person into a working session.
        */}
        <ErrorBoundary key={uid ?? "signed-out"}>
        {/* undefined means Firebase is still restoring the session. Rendering the
            sign-in form here would flash it at every signed-in user on reload. */}
        {user === undefined ? (
          <p className="muted" role="status">Loading…</p>
        ) : user === null ? (
          <SignedOut onHalfCreated={setHalfCreated} />
        ) : (
          <Switch>
            <Route path="/">{() => <Home halfCreated={halfCreated} />}</Route>
            <Route path="/professional" component={Professional} />
            {/*
              One case-detail URL for every role, not one per portal. The server
              scopes it: getCaseForActor runs the row through authorize(case:read),
              so a client reaches their own case, an assigned attorney reaches
              theirs, an admin reaches any, and anyone else gets a refusal rather
              than a body. Three near-identical routes would be three places for
              that scoping to be re-guessed in the browser.
            */}
            <Route path="/cases/:id" component={CaseDetail} />
            {/*
              Reachable by every signed-in role, not just clients. An attorney
              uploads registry extracts and a search agent would too; what the
              platform holds and how long it holds it is their question as much
              as the client's.
            */}
            <Route path="/data" component={DataHandling} />
            {/*
              EVERY ADMIN ROUTE IS GATED, in one place, on purpose.
              ---------------------------------------------------------------
              These routes previously carried comments saying a client-side gate
              was unnecessary — "hiding is a courtesy, the server refuses
              regardless". That was true of the endpoints those comments were
              written about, and false of a class the router cannot distinguish.

              `/admin/cases` reads GET /api/cases, which is role-SCOPED rather
              than admin-only. A client was not refused; they were served their
              own cases, correctly, with 200 — and the screen then told them, in
              the product's own words, that they were looking at "every case on
              the platform". `/admin/roster` reads the same endpoint and is safe
              today only because two sibling admin queries 403 first, which is
              protection by accident.

              So the gate is structural now rather than remembered per screen. A
              new admin route added inside this wrapper inherits it; one added
              outside is visibly outside. AdminOnly.tsx carries the full
              reasoning, including why it is NOT the security boundary — the
              server's authorize() chokepoint still is, and is unchanged.
            */}
            <Route path="/admin" component={() => <AdminOnly><AdminHome /></AdminOnly>} />
            <Route path="/admin/cases" component={() => <AdminOnly><AdminCases /></AdminOnly>} />
            <Route
              path="/admin/professionals"
              component={() => <AdminOnly><AdminProfessionals /></AdminOnly>}
            />
            <Route path="/admin/roster" component={() => <AdminOnly><AdminRoster /></AdminOnly>} />
            <Route path="/admin/reports" component={() => <AdminOnly><AdminReports /></AdminOnly>} />
            <Route
              path="/admin/registry"
              component={() => <AdminOnly><AdminRegistry /></AdminOnly>}
            />
            {/* The pool the mechanical assignment computes over. */}
            <Route path="/admin/panel" component={() => <AdminOnly><AdminPanel /></AdminOnly>} />
            {/* The door to POST /admin/users/:id/role. */}
            <Route path="/admin/access" component={() => <AdminOnly><AdminAccess /></AdminOnly>} />
            {/*
              A bare "Not found." was a dead end with no way out but the back
              button, and it also over-claimed: this router does not know whether
              the address is wrong or whether it names a real case belonging to
              somebody else. It says what it knows — no screen at this address —
              and offers the way back.
            */}
            <Route>
              <section>
                <header className="page-head">
                  <p className="eyebrow">No such screen</p>
                  <h2>There is nothing at this address</h2>
                </header>
                <p className="empty">
                  This application has no screen at the address you asked for. That is all this
                  means — it is not a statement about a case, a document or an account, and
                  nothing has gone wrong with any of them.
                </p>
                <p>
                  <Link href="/">Go back to your account</Link>
                </p>
              </section>
            </Route>
          </Switch>
        )}
        </ErrorBoundary>
      </main>

      <SiteFooter />
    </div>
  );
}
